import { Controller } from "@hotwired/stimulus";

type Contact = { name: string; email: string };

/** A row keeps the wire value separate from the address currently being composed. */
export default class extends Controller<HTMLElement> {
  static targets = ["input", "value", "tokens", "menu", "template", "status"];
  static values = { url: String, required: Boolean, invalid: String, unavailable: String };
  declare readonly inputTarget: HTMLInputElement;
  declare readonly valueTarget: HTMLInputElement;
  declare readonly tokensTarget: HTMLElement;
  declare readonly menuTarget: HTMLElement;
  declare readonly templateTarget: HTMLTemplateElement;
  declare readonly statusTarget: HTMLElement;
  declare readonly urlValue: string;
  declare readonly requiredValue: boolean;
  declare readonly invalidValue: string;
  declare readonly unavailableValue: string;
  private timer?: ReturnType<typeof setTimeout>;
  private request?: AbortController;
  private contacts: Contact[] = [];
  private active = -1;
  private validator = document.createElement("input");
  private resizeObserver?: ResizeObserver;

  private positionMenu = (): void => {
    const parent = this.menuTarget.offsetParent;
    if (this.menuTarget.hidden || !parent) return;
    const input = this.inputTarget;
    const inputRect = input.getBoundingClientRect();
    const origin = parent.getBoundingClientRect();
    const container = this.element.closest<HTMLElement>("#tickets-detail-action-scroll")!;
    const panel = container.getBoundingClientRect();
    const form = this.element.closest("form")?.getBoundingClientRect() ?? panel;
    const style = getComputedStyle(input);
    // Measure the text before the caret with the input's actual typography.
    const mirror = document.createElement("span");
    Object.assign(mirror.style, { position: "fixed", visibility: "hidden", whiteSpace: "pre", font: style.font, letterSpacing: style.letterSpacing });
    const caretOffset = input.selectionDirection === "backward" ? input.selectionStart : input.selectionEnd;
    mirror.textContent = input.value.slice(0, caretOffset ?? input.value.length);
    document.body.append(mirror);
    const caret = inputRect.left + parseFloat(style.paddingLeft) + mirror.getBoundingClientRect().width - input.scrollLeft;
    mirror.remove();
    const left = Math.max(8, form.left + 8, panel.left + container.clientLeft + 8);
    const right = Math.min(window.innerWidth - 8, form.right - 8, panel.left + container.clientLeft + container.clientWidth - 8);
    const width = Math.min(384, Math.max(0, right - left));
    const x = Math.max(left, Math.min(caret, inputRect.right, right - width));
    Object.assign(this.menuTarget.style, {
      left: `${x - origin.left}px`,
      top: `${inputRect.bottom - origin.top}px`,
      width: `${width}px`,
      maxHeight: `${Math.max(0, Math.min(272, panel.bottom - inputRect.bottom - 8, window.innerHeight - inputRect.bottom - 8))}px`,
    });
  };

  private revealMenu = (): void => {
    if (this.menuTarget.hidden || !this.menuTarget.offsetParent) return;
    this.positionMenu();
    const panel = this.element.closest<HTMLElement>("#tickets-detail-action-scroll")!;
    const bounds = panel.getBoundingClientRect();
    // Make room below the caret instead of flipping the suggestions above it.
    this.menuTarget.style.maxHeight = `${Math.max(0, Math.min(272, bounds.height - this.inputTarget.offsetHeight - 16))}px`;
    panel.scrollTop += Math.max(0, this.menuTarget.getBoundingClientRect().bottom - Math.min(bounds.bottom, window.innerHeight) + 8);
    this.positionMenu();
  };

  connect(): void {
    this.validator.type = "email";
    this.inputTarget.inputMode = "email";
    this.validate();
    document.addEventListener("selectionchange", this.positionMenu);
    window.addEventListener("scroll", this.positionMenu, true);
    window.addEventListener("resize", this.revealMenu);
    this.resizeObserver = new ResizeObserver(this.revealMenu);
    this.resizeObserver.observe(this.tokensTarget);
    this.resizeObserver.observe(this.element.closest("#tickets-detail-action-scroll")!);
  }

  disconnect(): void {
    clearTimeout(this.timer);
    this.request?.abort();
    document.removeEventListener("selectionchange", this.positionMenu);
    window.removeEventListener("scroll", this.positionMenu, true);
    window.removeEventListener("resize", this.revealMenu);
    this.resizeObserver?.disconnect();
  }

  focus(event: MouseEvent): void {
    if (event.target instanceof Element && !event.target.closest("button, [role=option]")) {
      const chip = event.target.closest(".email-recipient");
      if (chip && window.matchMedia("(hover: none)").matches) {
        chip.querySelector("button")?.focus();
        return;
      }
      this.inputTarget.focus();
      if (this.menuTarget.hidden) this.search(event);
    }
  }

  search(event: Event): void {
    event.stopPropagation();
    this.close();
    this.validate();
    if (/[,;\n]/.test(this.inputTarget.value) && this.commit()) return;
    const query = this.inputTarget.value.trim();
    this.timer = setTimeout(() => { void this.suggest(query); }, query ? 150 : 0);
  }

  private async suggest(query: string): Promise<void> {
    const request = new AbortController();
    this.request = request;
    try {
      const url = new URL(this.urlValue, window.location.origin);
      url.searchParams.set("q", query);
      url.searchParams.set("exclude", this.emails().join(","));
      const response = await fetch(url, { signal: request.signal, headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("Contact search unavailable");
      const result: unknown = await response.json();
      if (request.signal.aborted || this.inputTarget.value.trim() !== query || document.activeElement !== this.inputTarget) return;
      if (!Array.isArray(result)) throw new Error("Invalid contact response");
      const selected = new Set(this.emails().map(email => email.toLowerCase()));
      this.contacts = result.filter((value): value is Contact =>
        typeof value === "object" && value !== null && typeof value.email === "string" && typeof value.name === "string",
      ).filter(contact => {
        const key = contact.email.toLowerCase();
        if (selected.has(key)) return false;
        selected.add(key);
        return true;
      });
      this.menuTarget.replaceChildren();
      this.contacts.forEach((contact, index) => {
        const option = document.createElement("div");
        option.id = `${this.menuTarget.id}-${index}`;
        option.className = "email-recipient-option";
        option.setAttribute("role", "option");
        option.setAttribute("aria-selected", "false");
        option.dataset.posthogId = "tickets-email-recipient-suggestion";
        const avatar = document.createElement("span");
        avatar.className = "email-recipient-avatar";
        avatar.setAttribute("aria-hidden", "true");
        avatar.textContent = (contact.name || contact.email).slice(0, 1).toUpperCase();
        const text = document.createElement("span");
        text.className = "email-recipient-option-text";
        const name = document.createElement("span");
        name.textContent = contact.name || contact.email;
        const email = document.createElement("small");
        email.textContent = contact.email;
        text.append(name, email);
        option.append(avatar, text);
        option.addEventListener("pointerdown", event => event.preventDefault());
        option.addEventListener("click", () => this.choose(index));
        this.menuTarget.append(option);
      });
      this.menuTarget.hidden = this.contacts.length === 0;
      this.revealMenu();
      this.inputTarget.setAttribute("aria-expanded", String(this.contacts.length > 0));
    } catch {
      if (!request.signal.aborted) this.statusTarget.textContent = this.unavailableValue;
    }
  }

  keydown(event: KeyboardEvent): void {
    if (event.isComposing) return;
    if ((event.key === "ArrowDown" || event.key === "ArrowUp") && !this.menuTarget.hidden) {
      event.preventDefault();
      const length = this.contacts.length;
      this.active = this.active < 0
        ? (event.key === "ArrowDown" ? 0 : length - 1)
        : (this.active + (event.key === "ArrowDown" ? 1 : -1) + length) % length;
      Array.from(this.menuTarget.children).forEach((option, index) => option.setAttribute("aria-selected", String(index === this.active)));
      const option = this.menuTarget.children[this.active];
      this.inputTarget.setAttribute("aria-activedescendant", option.id);
      option.scrollIntoView({ block: "nearest" });
    } else if (event.key === "Escape" && !this.menuTarget.hidden) {
      event.preventDefault();
      this.close();
    } else if (event.key === "Enter" || event.key === "," || event.key === ";" || (event.key === "Tab" && this.inputTarget.value.trim())) {
      if (event.key !== "Tab") event.preventDefault();
      if (this.active >= 0 && !this.menuTarget.hidden) this.choose(this.active);
      else if (!this.commit()) {
        event.preventDefault();
        this.inputTarget.reportValidity();
      }
    } else if (event.key === "Backspace" && !this.inputTarget.value) {
      const last = this.tokensTarget.querySelector<HTMLButtonElement>(".email-recipient:last-of-type button");
      if (last) { event.preventDefault(); last.focus(); }
    }
  }

  blur(): void {
    this.commit();
    this.close();
  }

  commit(event?: Event): boolean {
    if (event?.type === "email-recipients-commit" && document.activeElement === this.inputTarget
      && !(event instanceof CustomEvent && event.detail?.commitFocused)) return true;
    const raw = this.inputTarget.value.trim();
    if (!raw) { this.validate(); return true; }
    const emails = raw.split(/[,;\n]+/).map(value => value.trim()).filter(Boolean);
    if (!emails.length || emails.some(email => { this.validator.value = email; return !this.validator.checkValidity(); })) {
      this.inputTarget.setCustomValidity(this.invalidValue);
      return false;
    }
    emails.forEach(email => this.add(email));
    this.inputTarget.value = "";
    this.close();
    this.sync();
    return true;
  }

  remove(event: Event): void {
    const button = event.currentTarget as HTMLElement;
    button.closest(".email-recipient")?.remove();
    this.inputTarget.focus();
    this.sync();
  }

  private choose(index: number): void {
    const contact = this.contacts[index];
    if (!contact) return;
    this.add(contact.email);
    this.inputTarget.value = "";
    this.close();
    this.sync();
    this.inputTarget.focus();
  }

  private add(email: string): void {
    if (this.emails().some(existing => existing.toLowerCase() === email.toLowerCase())) return;
    const chip = this.templateTarget.content.firstElementChild!.cloneNode(true) as HTMLElement;
    chip.dataset.email = email;
    chip.title = email;
    chip.querySelector(".email-recipient-label")!.textContent = email;
    const remove = chip.querySelector("button")!;
    remove.setAttribute("aria-label", `${remove.getAttribute("aria-label")!.trim()} ${email}`);
    this.tokensTarget.insertBefore(chip, this.inputTarget);
  }

  private emails(): string[] {
    return Array.from(this.tokensTarget.querySelectorAll<HTMLElement>(".email-recipient"), chip => chip.dataset.email!);
  }

  private sync(): void {
    this.valueTarget.value = this.emails().join(", ");
    this.validate();
    this.valueTarget.dispatchEvent(new Event("input", { bubbles: true }));
  }

  private validate(): void {
    this.inputTarget.style.flexBasis = this.inputTarget.value ? "8rem" : "1rem";
    this.inputTarget.setCustomValidity((this.requiredValue && !this.emails().length) ? this.invalidValue : "");
  }

  private close(): void {
    clearTimeout(this.timer);
    this.request?.abort();
    this.contacts = [];
    this.active = -1;
    this.menuTarget.hidden = true;
    this.statusTarget.textContent = "";
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.inputTarget.removeAttribute("aria-activedescendant");
  }
}
